import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useProject } from '@/contexts/project-context'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Calendar,
  Award
} from 'lucide-react'

interface ContributionData {
  user_id: string
  user_name: string
  total_score: number
  effort_score: number
  capital_score: number
  task_count: number
  is_founder: boolean
  percentage_of_total: number
  rank: number
}

interface ContributionSnapshotProps {
  userId: string
  onDataLoaded?: (data: ContributionData | null) => void
}

export default function ContributionSnapshot({ userId, onDataLoaded }: ContributionSnapshotProps) {
  const [userData, setUserData] = useState<ContributionData | null>(null)
  const [allContributors, setAllContributors] = useState<ContributionData[]>([])
  const [loading, setLoading] = useState(true)
  const { currentProject } = useProject()

  useEffect(() => {
    if (userId && currentProject) {
      fetchContributionData()
    }
  }, [userId, currentProject])

  const fetchContributionData = async () => {
    if (!currentProject?.id || !userId) return

    setLoading(true)
    try {
      // Fetch all contributions for this project
      const { data: contributions, error } = await supabase
        .from('contributions')
        .select(`
          *,
          users (id, first_name, last_name, email, role_id),
          tasks (
            id, 
            tshirt_size_effort, 
            tshirt_size_capital, 
            frequency, 
            founder_leverage
          )
        `)
        .eq('project_id', currentProject.id)

      if (error) throw error

      // Fetch founder role id
      const { data: founderRole } = await supabase
        .from('roles')
        .select('id')
        .eq('name', 'founder')
        .single()

      // Calculate scores for each user
      const userScores: Record<string, ContributionData> = {}
      
      // Weight mappings
      const effortWeights: Record<string, number> = { S: 1, M: 3, L: 8, XL: 13 }
      const capitalWeights: Record<string, number> = { S: 1, M: 5, L: 10, XL: 20 }
      const frequencyMultipliers: Record<string, number> = {
        'One-time': 1,
        'Daily': 20,
        'Weekly': 4,
        'Monthly': 2,
        'Quarterly': 1.5,
        'Ongoing': 10,
      }

      contributions?.forEach((contrib: any) => {
        const uid = contrib.user_id
        const task = contrib.tasks

        if (!userScores[uid]) {
          userScores[uid] = {
            user_id: uid,
            user_name: contrib.users ? 
              `${contrib.users.first_name || ''} ${contrib.users.last_name || ''}`.trim() || contrib.users.email :
              'Unknown',
            total_score: 0,
            effort_score: 0,
            capital_score: 0,
            task_count: 0,
            is_founder: contrib.users?.role_id === founderRole?.id,
            percentage_of_total: 0,
            rank: 0,
          }
        }

        const effortWeight = effortWeights[task?.tshirt_size_effort] || 1
        const capitalWeight = capitalWeights[task?.tshirt_size_capital] || 1
        const frequencyMultiplier = frequencyMultipliers[task?.frequency] || 1
        const founderMultiplier = task?.founder_leverage && userScores[uid].is_founder ? 1.2 : 1

        const effortScore = (contrib.effort_value || 0) * effortWeight * frequencyMultiplier * founderMultiplier
        const capitalScore = (contrib.capital_value || 0) * capitalWeight * 0.001 * founderMultiplier

        userScores[uid].effort_score += effortScore
        userScores[uid].capital_score += capitalScore
        userScores[uid].total_score += effortScore + capitalScore
        userScores[uid].task_count += 1
      })

      // Convert to array and calculate percentages
      const contributorArray = Object.values(userScores)
      const totalScore = contributorArray.reduce((sum, user) => sum + user.total_score, 0)

      contributorArray.forEach(user => {
        user.percentage_of_total = totalScore > 0 ? (user.total_score / totalScore) * 100 : 0
      })

      // Sort by total score and assign ranks
      contributorArray.sort((a, b) => b.total_score - a.total_score)
      contributorArray.forEach((user, index) => {
        user.rank = index + 1
      })

      setAllContributors(contributorArray)
      
      const currentUserData = contributorArray.find(u => u.user_id === userId)
      setUserData(currentUserData || null)
      onDataLoaded?.(currentUserData || null)

    } catch (error) {
      console.error('Error fetching contribution data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    )
  }

  if (!userData) {
    return (
      <Card>
        <CardContent className="text-center py-8 text-gray-500">
          No contribution data available for this user
        </CardContent>
      </Card>
    )
  }

  const topContributors = allContributors.slice(0, 5)
  const userInTop5 = userData.rank <= 5

  return (
    <div className="space-y-4">
      {/* User's Contribution Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Contribution Summary</span>
            {userData.is_founder && (
              <Badge variant="secondary" className="ml-2">
                <Award className="h-3 w-3 mr-1" />
                Founder
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Total Score</p>
              <p className="text-2xl font-bold">{userData.total_score.toFixed(0)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Rank</p>
              <p className="text-2xl font-bold">#{userData.rank}</p>
              <p className="text-xs text-gray-400">of {allContributors.length}</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Share of Total</span>
              <span className="font-medium">{userData.percentage_of_total.toFixed(2)}%</span>
            </div>
            <Progress value={userData.percentage_of_total} className="h-2" />
          </div>

          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="text-center">
              <TrendingUp className="h-4 w-4 mx-auto mb-1 text-blue-600" />
              <p className="text-xs text-gray-500">Effort</p>
              <p className="text-sm font-medium">{userData.effort_score.toFixed(0)}</p>
            </div>
            <div className="text-center">
              <DollarSign className="h-4 w-4 mx-auto mb-1 text-green-600" />
              <p className="text-xs text-gray-500">Capital</p>
              <p className="text-sm font-medium">{userData.capital_score.toFixed(0)}</p>
            </div>
            <div className="text-center">
              <Calendar className="h-4 w-4 mx-auto mb-1 text-purple-600" />
              <p className="text-xs text-gray-500">Tasks</p>
              <p className="text-sm font-medium">{userData.task_count}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Contributors Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Users className="h-4 w-4 mr-2" />
            Top Contributors
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {topContributors.map((contributor) => {
              const isCurrentUser = contributor.user_id === userId
              return (
                <div
                  key={contributor.user_id}
                  className={`flex items-center justify-between py-2 px-3 rounded ${
                    isCurrentUser ? 'bg-blue-50 border border-blue-200' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="font-medium text-sm w-6">#{contributor.rank}</div>
                    <div>
                      <p className="text-sm font-medium">
                        {contributor.user_name}
                        {contributor.is_founder && (
                          <Badge variant="outline" className="ml-2 text-xs">Founder</Badge>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {contributor.task_count} tasks • {contributor.percentage_of_total.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{contributor.total_score.toFixed(0)}</p>
                    <p className="text-xs text-gray-500">points</p>
                  </div>
                </div>
              )
            })}
            {!userInTop5 && userData && (
              <>
                <div className="text-center text-gray-400 text-xs">...</div>
                <div className="flex items-center justify-between py-2 px-3 rounded bg-blue-50 border border-blue-200">
                  <div className="flex items-center space-x-3">
                    <div className="font-medium text-sm w-6">#{userData.rank}</div>
                    <div>
                      <p className="text-sm font-medium">
                        {userData.user_name}
                        {userData.is_founder && (
                          <Badge variant="outline" className="ml-2 text-xs">Founder</Badge>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {userData.task_count} tasks • {userData.percentage_of_total.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{userData.total_score.toFixed(0)}</p>
                    <p className="text-xs text-gray-500">points</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}